import React, { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { GlobalContext } from "../../../../context/Provider";
import ProfilePlaceholder from "../../../../assets/images/profile_placeholder.png";
import henceforthApi from "../../../../utils/henceforthApi";
import { ROLES_STAFF_MEMBERS } from "../../../../constants/actionTypes";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../../ui/dropdown-menu";
import { User, Users, KeyRound, LogOut, ChevronDown } from "lucide-react";
import { Button } from "../../../ui/button";

interface ProfileDropdownProps {
  roles?: string[];
  logOutNow?: () => void;
  align?: "start" | "end" | "center";
}

const ProfileDropdown: React.FC<ProfileDropdownProps> = ({
  roles = [],
  align = "end",
}) => {
  const { authState, logOutNow } = useContext(GlobalContext);
  const navigate = useNavigate();

  if (authState.access_token) {
    henceforthApi.setToken(authState.access_token);
  }

  const profileImg = authState.image
    ? `${henceforthApi.API_FILE_ROOT_ORIGINAL}${authState.image}`
    : ProfilePlaceholder;

  const displayName = authState.name
    ? authState.name.charAt(0).toUpperCase() + authState.name.slice(1)
    : "Admin";

  const isSuperAdmin = Boolean(authState.super_admin);
  const roleLabel = isSuperAdmin ? "Super Admin" : "Administrator";

  const handleLogout = () => {
    if (typeof logOutNow === "function") {
      logOutNow();
    }
  };

  return (
    <div className="relative inline-flex items-center">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            className="flex items-center gap-2.5 py-1.5 px-2.5 rounded-xl borde-0 border-slate-200/90 bg-white hover:bg-slate-50 transition-all text-left focus:outline-none focus:ring-2 focus:ring-[#006747]/20 group cursor-pointer"
          >
            <div className="flex items-center gap-2 min-w-0">
              <div className="relative shrink-0 flex items-center justify-center">
                <img
                  src={profileImg}
                  alt={displayName}
                  className="w-8 h-8 rounded-full object-cover border border-slate-200 bg-white shadow-2xs"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = ProfilePlaceholder;
                  }}
                />
              </div>
              <div className="hidden sm:flex flex-col min-w-0 pr-1">
                <span className="text-xs font-bold text-slate-800 truncate leading-tight group-hover:text-[#006747] transition-colors">
                  {displayName}
                </span>
                <span className="text-[10px] text-slate-400 font-medium leading-tight">
                  {roleLabel}
                </span>
              </div>
            </div>
            <ChevronDown className="w-3.5 h-3.5 text-slate-400 group-hover:text-slate-600 transition-colors shrink-0" />
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          align={align}
          className="w-56 p-1.5 shadow-lg border border-slate-200 rounded-xl bg-white z-50"
        >
          <DropdownMenuLabel className="px-2.5 py-2">
            <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider block">
              Signed in as
            </span>
            <span className="text-sm font-bold text-slate-800 truncate block mt-0.5">
              {displayName}
            </span>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />

          <DropdownMenuItem
            onClick={() => navigate("/profile")}
            className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-xs text-slate-700 hover:text-slate-900 cursor-pointer font-medium"
          >
            <User className="w-4 h-4 text-slate-500" />
            <span>My Profile</span>
          </DropdownMenuItem>

          <DropdownMenuItem
            onClick={() => navigate("/change-password")}
            className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-xs text-slate-700 hover:text-slate-900 cursor-pointer font-medium"
          >
            <KeyRound className="w-4 h-4 text-slate-500" />
            <span>Change Password</span>
          </DropdownMenuItem>

          {(roles.includes(ROLES_STAFF_MEMBERS) || isSuperAdmin) && (
            <DropdownMenuItem
              onClick={() => navigate("/staff/1/all")}
              className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-xs text-slate-700 hover:text-slate-900 cursor-pointer font-medium"
            >
              <Users className="w-4 h-4 text-slate-500" />
              <span>Staff Management</span>
            </DropdownMenuItem>
          )}

          <DropdownMenuSeparator />

          <DropdownMenuItem
            onClick={handleLogout}
            className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-xs text-rose-600 hover:text-rose-700 hover:bg-rose-50 cursor-pointer font-medium"
          >
            <LogOut className="w-4 h-4 text-rose-500" />
            <span>Log out</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default ProfileDropdown;
